import {
    createEntityAdapter,
    createSelector,
    createSlice,
    PayloadAction,
} from '@reduxjs/toolkit'
import { SchoolBookingKey } from '../firebase/types-bookings'
import { RootState } from './store'
import dayjs from '../helpers/dayjs'
import { TeacherEmploymentSetting } from './types'
import { SchoolEventType } from '../components/Snackbar'

export type BookingConfig = {
    date: string
    placementUid: string
    teacherUid: string
    interest: TeacherEmploymentSetting
    isSupportStaff?: boolean
}

interface SchoolBookingsState {
    entities: Record<string, SchoolBookingKey>
    ids: string[]
}

// interface ISchoolBookingsSlice {
//     searchText: string
//     filters: {
//         status?: BookingStatusCellType
//         type?: SchoolBookingType
//         term?: BookingTerm
//         date?: string
//     }

//     // schoolKeys: {
//     //     thisYear?: SchoolBookingKeys
//     //     nextYear?: SchoolBookingKeys
//     // }
//     schoolKeys: SchoolBookingsState
//     // schoolKeysNextYear?: SchoolBookingKeys
// }

export type SchoolBookingKeyEntity = SchoolBookingKey & {
    id: string
    date: string
    teacherUid: string
}

export const makeEntity = (
    date: string,
    teacherUid: string,
    keyData: SchoolBookingKey
): SchoolBookingKeyEntity => {
    const normalizedKey: SchoolBookingKey = {
        placementUid: keyData.placementUid,
        cancelled: keyData.cancelled ?? undefined,
        notified: keyData.notified ?? undefined,
        cancelledByTeacher: keyData.cancelledByTeacher ?? undefined,
        notifiedOfCancellation: keyData.notifiedOfCancellation ?? undefined,
        cancelNotification: keyData.cancelNotification ?? undefined,
        claimedByTeacher: keyData.claimedByTeacher ?? undefined,
        replacementNotification: keyData.replacementNotification ?? undefined,
        replaced: keyData.replaced ?? undefined,
        esBooking: keyData.esBooking ?? undefined,
    }

    return {
        id: `${date}-${teacherUid}`,
        date,
        teacherUid,
        ...normalizedKey,
    }
}

const schoolKeysAdapter = createEntityAdapter<SchoolBookingKeyEntity>({
    selectId: (booking) => booking.id,
    sortComparer: (a, b) =>
        a.date.localeCompare(b.date) ||
        a.teacherUid.localeCompare(b.teacherUid),
})

export const initialState = schoolKeysAdapter.getInitialState()

export const schoolBookingsSlice = createSlice({
    name: 'schoolBookings',
    initialState,
    reducers: {
        upsertSchoolBookingKeys: (
            state,
            action: PayloadAction<SchoolBookingKeyEntity[]>
        ) => {
            schoolKeysAdapter.upsertMany(state, action.payload)
        },
        upsertSchoolBookingKey: (
            state,
            action: PayloadAction<SchoolBookingKeyEntity>
        ) => {
            schoolKeysAdapter.upsertOne(state, action.payload)
        },
        removeSchoolBookingKey: (
            state,
            action: PayloadAction<string> // id
        ) => {
            schoolKeysAdapter.removeOne(state, action.payload)
        },
        removeSchoolBookingKeysByDate: (
            state,
            action: PayloadAction<string> // date
        ) => {
            const date = action.payload
            const idsToRemove = state.ids.filter((id) =>
                (state.entities[id] as any).date.startsWith(date)
            )
            schoolKeysAdapter.removeMany(state, idsToRemove)
        },

        resetSchoolBookings: () => {
            return initialState
        },
        // setSearchText: (state, action: PayloadAction<string>) => {
        //     state.searchText = action.payload
        // },
        // setFilter: (
        //     state,
        //     action: PayloadAction<Partial<ISchoolBookingsSlice['filters']>>
        // ) => {
        //     Object.assign(state.filters, action.payload)
        // },
        // setSchoolBookingKeys: (
        //     state,
        //     action: PayloadAction<SchoolBookingKeys>So
        // ) => {
        //     state.schoolKeys = action.payload
        // },
    },
})

export const selectCancelledKeys = (state: RootState) =>
    selectAllSchoolBookingKeys(state).filter((el) => el.cancelled)

export const {
    selectAll: selectAllSchoolBookingKeys,
    selectById: selectSchoolBookingKeyById,
    selectIds: selectSchoolBookingKeyIds,
} = schoolKeysAdapter.getSelectors((state: RootState) => state.schoolBookings)

/**
 * Creates a selector fn that returns school keys by given date
 * @param state
 * @param date Formatted as YYYY-MM-DD
 * @returns
 */
export const makeSelectBookingsForDate = () =>
    createSelector(
        [selectAllSchoolBookingKeys, (_: RootState, date: string) => date],
        (bookings, date) => bookings.filter((booking) => booking.date === date)
    )

export const makeSelectHasMobileViewedBooking = (id: string) =>
    createSelector(
        [
            (state: RootState) =>
                selectSchoolBookingKeyById(state, id),
            (_, schoolUserUid: string, type: SchoolEventType) => ({
                schoolUserUid,
                type,
            }),
        ],
        (key, { schoolUserUid, type }) => {
            switch (type) {
                case 'BOOKING_CANCELLED_BY_TEACHER':
                case 'BOOKING_CANCELLED_BY_SCHOOL':
                case 'BOOKING_CANCELLED_REPLACING': {
                    return Boolean(key?.cancelNotification?.[schoolUserUid])
                }
                case 'BOOKING_REPLACED': {
                    return Boolean(
                        key?.replacementNotification?.[schoolUserUid]
                    )
                }
                default: {
                    return false
                }
                // case 'BOOKING_CONFIRMED':
            }
        }
    )

/**
 * Creates a selector fn that returns school keys by given date
 * @param state
 * @param date Formatted as YYYY-MM-DD
 * @returns
 */
export const selectFromTodayKeys = createSelector(
    [selectAllSchoolBookingKeys],
    (bookings) => {
        const today = dayjs().format('YYYY-MM-DD')
        return bookings.filter((booking) => booking.date >= today)
    }
)

export const makeSelectBookingsBySetting = () =>
    createSelector(
        [
            selectAllSchoolBookingKeys,
            (_: RootState, setting: 'school' | 'supportStaff') => setting,
        ],
        (bookings, setting) => {
            const today = dayjs().tz()
            return bookings.filter((booking) => {
                const isTodayOrFuture = today.isSameOrBefore(
                    dayjs.tz(booking.date, 'YYYY-MM-DD', 'Australia/Melbourne'),
                    'day'
                )
                const matchesSetting =
                    setting === 'supportStaff'
                        ? !!booking.esBooking
                        : !booking.esBooking

                return isTodayOrFuture && matchesSetting
            })
        }
    )

export const {
    upsertSchoolBookingKey,
    upsertSchoolBookingKeys,
    removeSchoolBookingKey,
    removeSchoolBookingKeysByDate,
    resetSchoolBookings,
} = schoolBookingsSlice.actions

export default schoolBookingsSlice.reducer
